/**
 * Safe announce utility that handles browser compatibility issues with ariaNotify
 * Provides fallbacks for browsers that don't support the API or have implementation issues
 */

export function safeAnnounce(message: string, priority: 'normal' | 'assertive' = 'normal'): void {
    try {
        // Try using the native ariaNotify API with proper priority values
        if ('ariaNotify' in document && typeof document.ariaNotify === 'function') {
            // Map our priority to valid AriaNotifyPriority values
            const validPriority = priority === 'assertive' ? 'important' : 'normal';
            document.ariaNotify(message, { priority: validPriority });
            return;
        }
    } catch (error) {
        console.warn('ariaNotify failed, falling back to screen reader alternative:', error);
    }

    // Fallback: Create a temporary live region for screen readers
    try {
        const liveRegion = document.createElement('div');
        liveRegion.setAttribute('aria-live', priority);
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.setAttribute('class', 'sr-only');
        liveRegion.style.position = 'absolute';
        liveRegion.style.left = '-10000px';
        liveRegion.style.width = '1px';
        liveRegion.style.height = '1px';
        liveRegion.style.overflow = 'hidden';
        
        document.body.appendChild(liveRegion);
        
        // Add the message
        liveRegion.textContent = message;
        
        // Clean up after announcement
        setTimeout(() => {
            if (liveRegion.parentNode) {
                liveRegion.parentNode.removeChild(liveRegion);
            }
        }, 1000);
    } catch (fallbackError) {
        console.warn('Screen reader fallback also failed:', fallbackError);
        // Last resort: console log for debugging
        console.log('Accessibility announcement:', message);
    }
}